import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Mail, LogOut, Scale } from 'lucide-react';
import { auth, user as userApi } from '../utils/api';
import BottomNav from '../components/BottomNav';
import LoadingSpinner from '../components/LoadingSpinner';
import PageHeader from '../components/PageHeader';

const ProfilePage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [measurementSystem, setMeasurementSystem] = useState('imperial');
  const [savingMeasurement, setSavingMeasurement] = useState(false);

  useEffect(() => {
    fetchUser();
  }, []);

  const fetchUser = async () => {
    try {
      const response = await auth.getCurrentUser();
      setUser(response.data);
      setMeasurementSystem(response.data?.measurement_system || 'imperial');
    } catch (error) {
      console.error('Failed to fetch user:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMeasurementToggle = async (system) => {
    setSavingMeasurement(true);
    try {
      await userApi.updateMeasurementSystem(system);
      setMeasurementSystem(system);
    } catch (error) {
      console.error('Failed to update measurement system:', error);
    } finally {
      setSavingMeasurement(false);
    }
  };

  const handleLogout = async () => {
    if (!window.confirm('Are you sure you want to log out?')) return;
    
    try {
      await auth.logout();
      localStorage.removeItem('newRecipeConversation');
      localStorage.removeItem('currentChatId');
      localStorage.removeItem('recipeBoxSort');
      localStorage.removeItem('recipeBoxView');
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
      navigate('/');
    }
  };

  if (loading) {
    return (
      <LoadingSpinner message="Loading profile..." fullScreen />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <PageHeader title="Profile" />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* User Info Card */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center gap-4 mb-6">
            {user?.picture ? (
              <img
                src={user.picture}
                alt={user.name}
                className="w-16 h-16 rounded-full"
              />
            ) : (
              <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center">
                <User size={32} className="text-amber-600" />
              </div>
            )}
            <div>
              <h2 className="text-xl font-semibold text-gray-900">{user?.name || 'User'}</h2>
              <p className="text-gray-500">Recipe enthusiast</p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center gap-3 text-gray-700">
              <User size={20} className="text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Name</p>
                <p className="font-medium">{user?.name || 'Not set'}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3 text-gray-700">
              <Mail size={20} className="text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Email</p>
                <p className="font-medium">{user?.email || 'Not set'}</p>
              </div>
            </div>

            {/* Measurement System Toggle */}
            <div className="flex items-center gap-3 text-gray-700">
              <Scale size={20} className="text-gray-400" />
              <div className="flex-1">
                <p className="text-sm text-gray-500 mb-1.5">Measurements</p>
                <div className="flex gap-2" data-testid="measurement-toggle">
                  <button
                    onClick={() => handleMeasurementToggle('imperial')}
                    disabled={savingMeasurement}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      measurementSystem === 'imperial'
                        ? 'bg-amber-100 text-amber-800 border border-amber-300'
                        : 'bg-gray-100 text-gray-500 border border-gray-200 hover:bg-gray-200'
                    }`}
                    data-testid="measurement-imperial-btn"
                  >
                    US Standard
                  </button>
                  <button
                    onClick={() => handleMeasurementToggle('metric')}
                    disabled={savingMeasurement}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      measurementSystem === 'metric'
                        ? 'bg-amber-100 text-amber-800 border border-amber-300'
                        : 'bg-gray-100 text-gray-500 border border-gray-200 hover:bg-gray-200'
                    }`}
                    data-testid="measurement-metric-btn"
                  >
                    Metric
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  {measurementSystem === 'metric'
                    ? 'Grams, liters, Celsius'
                    : 'Cups, ounces, Fahrenheit'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Logout Button */}
        <button
          onClick={handleLogout}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-white text-red-600 rounded-lg shadow-sm hover:bg-red-50 transition-colors border border-red-200"
          data-testid="logout-btn"
        >
          <LogOut size={20} />
          <span className="font-medium">Log Out</span>
        </button>

        {/* App Info */}
        <div className="mt-8 text-center text-sm text-gray-400">
          <p>Honey v1.0</p>
          <p className="mt-1">Made with love by Emergent</p>
        </div>
      </main>

      <BottomNav />
    </div>
  );
};

export default ProfilePage;
